import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from "./authService";

// Query Keys
export const userKeys = {
  all: ['user'],
  profile: () => [...userKeys.all, 'profile'],
  preferences: () => [...userKeys.all, 'preferences'],
  notificationSettings: () => [...userKeys.all, 'notification-settings'],
};

// API Functions
const userApi = {
  // Get user profile
  getProfile: async () => {
    const response = await api.get('/user/profile');
    return response.data;
  },

  // Update user profile
  updateProfile: async (data) => {
    // Split name into first_name and last_name if needed
    const updateData = { ...data };
    if (data.name && !data.first_name && !data.last_name) {
      const nameParts = data.name.trim().split(' ');
      updateData.first_name = nameParts[0] || '';
      updateData.last_name = nameParts.slice(1).join(' ') || '';
      delete updateData.name;
    }
    const response = await api.put('/user/profile', updateData);
    return response.data;
  },

  // Get user preferences
  getPreferences: async () => {
    const response = await api.get('/user/preferences');
    return response.data;
  },

  // Update user preferences
  updatePreferences: async (data) => {
    const response = await api.put('/user/preferences', data);
    return response.data;
  },

  // Get notification settings
  getNotificationSettings: async () => {
    const response = await api.get('/notification-settings');
    return response.data;
  },

  // Update notification settings
  updateNotificationSettings: async (data) => {
    const response = await api.put('/notification-settings', data);
    return response.data;
  },

  // Change password
  changePassword: async (data) => {
    const response = await api.put('/auth/change-password', {
      current_password: data.currentPassword,
      new_password: data.newPassword,
      new_password_confirmation: data.confirmPassword
    });
    return response.data;
  },

  // Delete account (password optional for OAuth-only users)
  deleteAccount: async (data) => {
    const payload = {};
    if (data.reason != null && data.reason !== '') {
      payload.reason = data.reason;
    }
    if (data.password != null && String(data.password).trim() !== '') {
      payload.password = data.password;
    }
    const response = await api.post('/user/profile/delete', payload);
    return response.data;
  },
};

// React Query Hooks
export const useUserProfile = (options = {}) => {
  return useQuery({
    queryKey: userKeys.profile(),
    queryFn: () => userApi.getProfile(),
    ...options
  });
};

export const useUpdateUserProfile = (options = {}) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: userApi.updateProfile,
    onSuccess: (data, variables, context) => {
      // Invalidate and refetch profile data
      queryClient.invalidateQueries({ queryKey: userKeys.profile() });
      // Call custom onSuccess if provided
      if (options.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
    onError: (error, variables, context) => {
      // Call custom onError if provided
      if (options.onError) {
        options.onError(error, variables, context);
      }
    },
    ...options
  });
};

export const useUserPreferences = (options = {}) => {
  return useQuery({
    queryKey: userKeys.preferences(),
    queryFn: () => userApi.getPreferences(),
    ...options
  });
};

export const useUpdateUserPreferences = (options = {}) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: userApi.updatePreferences,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.preferences() });
    },
    ...options
  });
};

export const useNotificationSettings = (options = {}) => {
  return useQuery({
    queryKey: userKeys.notificationSettings(),
    queryFn: () => userApi.getNotificationSettings(),
    ...options
  });
};

export const useUpdateNotificationSettings = (options = {}) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: userApi.updateNotificationSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.notificationSettings() });
    },
    ...options
  });
};

export const useChangePassword = (options = {}) => {
  return useMutation({
    mutationFn: userApi.changePassword,
    ...options
  });
};

export const useDeleteAccount = (options = {}) => {
  return useMutation({
    mutationFn: userApi.deleteAccount,
    ...options
  });
};

